import React, { useEffect, useMemo, useState } from 'react'
import { Redirect } from 'react-router-dom'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core'
import PlayCircleFilledIcon from '@material-ui/icons/PlayCircleFilled'
import StopIcon from '@material-ui/icons/Stop'
import FreeBreakfastIcon from '@material-ui/icons/FreeBreakfast'
import RestaurantIcon from '@material-ui/icons/Restaurant'
import { useOfficeTimeTracker } from '../context/OfficeTimeTrackerContext'
import { EXECUTIVE_OFFICES } from '../constants/offices'
import {
  captureExecutiveStartLocation,
  getExecutiveLocationHelpCopy,
  isExecutiveLocationCaptureError
} from '../utils/locationCapture'
import {
  formatTimestamp,
  formatSeconds,
  getElapsedTodaySeconds,
  getRemainingWeekSeconds,
  getWeekDays,
  normalizeRole
} from '../utils/timeTrackerUi'
import { ExecutiveLocationType } from '../types'
import './timeTracker.css'

const TimeTrackerView: React.FC = () => {
  const {
    error,
    featureEnabled,
    fetchPausesForEntry,
    initialized,
    loading,
    normalizedRole,
    pausesByEntryId,
    pauseTimer,
    resumeTimer,
    session,
    startTimer,
    stopTimer,
    weekEntries
  } = useOfficeTimeTracker()
  const [ticker, setTicker] = useState(0)
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false)
  const [isPermissionDialogOpen, setIsPermissionDialogOpen] = useState(false)
  const [isCapturingLocation, setIsCapturingLocation] = useState(false)
  const [executiveLocationType, setExecutiveLocationType] = useState<ExecutiveLocationType | null>(null)
  const [permissionHelpCopy, setPermissionHelpCopy] = useState(getExecutiveLocationHelpCopy())

  const weekDays = useMemo(() => getWeekDays(session?.weekStartDate), [session?.weekStartDate])
  const normalizedTrackerRole = useMemo(() => normalizeRole(normalizedRole), [normalizedRole])
  const isExecutive = normalizedTrackerRole === 'ejecutivo'
  const todayKey = weekDays.find(day => day.isToday)?.key
  const [selectedDay, setSelectedDay] = useState<string>('')

  useEffect(() => {
    const defaultDay = todayKey || weekDays[0]?.key || ''

    if (!defaultDay) {
      return
    }

    setSelectedDay(currentSelectedDay => {
      if (!currentSelectedDay) {
        return defaultDay
      }

      const currentDayStillExists = weekDays.some(day => day.key === currentSelectedDay)
      return currentDayStillExists ? currentSelectedDay : defaultDay
    })
  }, [todayKey, weekDays])

  const selectedEntry = useMemo(() => weekEntries.find(entry => entry.workDate === selectedDay), [
    selectedDay,
    weekEntries
  ])
  const fallbackEntryId = selectedDay === todayKey ? session?.currentEntryId : null
  const displayEntryId = selectedEntry?.entryId || fallbackEntryId || null

  const pauses = displayEntryId ? pausesByEntryId[displayEntryId] || [] : []

  const getPauseLabel = (pauseType: 'break' | 'almuerzo') => (pauseType === 'break' ? 'Pausa activa' : 'Pausa almuerzo')

  useEffect(() => {
    if (displayEntryId) {
      void fetchPausesForEntry(displayEntryId)
    }
  }, [displayEntryId, fetchPausesForEntry, selectedEntry?.updatedAt])

  useEffect(() => {
    if (session?.status !== 'running') {
      return undefined
    }

    const intervalId = window.setInterval(() => {
      setTicker(previousTicker => previousTicker + 1)
    }, 1000)

    return () => {
      window.clearInterval(intervalId)
    }
  }, [session?.runningSince, session?.status])

  useEffect(() => {
    if (isExecutive && session?.canStart) {
      setExecutiveLocationType(null)
    }
  }, [isExecutive, session?.canStart, session?.currentDate])

  if (initialized && !featureEnabled) {
    return <Redirect to="/" />
  }

  const elapsedToday = getElapsedTodaySeconds(session)
  const remainingWeekSeconds = getRemainingWeekSeconds(session)
  void ticker

  const handleConfirmStop = async () => {
    await stopTimer()
    setIsStopDialogOpen(false)
  }

  const handleStartTimer = async () => {
    if (!isExecutive) {
      await startTimer()
      return
    }

    if (!executiveLocationType) {
      return
    }

    setIsCapturingLocation(true)

    try {
      const location = await captureExecutiveStartLocation(executiveLocationType)
      await startTimer(location)
    } catch (error) {
      if (isExecutiveLocationCaptureError(error)) {
        setPermissionHelpCopy(error.message)
        setIsPermissionDialogOpen(true)
      }
    } finally {
      setIsCapturingLocation(false)
    }
  }

  const renderExecutiveLocationSelector = () => {
    if (!isExecutive || !session?.canStart || selectedDay !== todayKey) {
      return null
    }

    return (
      <div className="time-tracker-location-card">
        <Typography variant="subtitle2" className="time-tracker-location-title">
          Elige la ubicacion desde la que vas a registrar tu ingreso para poder continuar.
        </Typography>
        <div className="time-tracker-location-toggle">
          <button
            type="button"
            className={`time-tracker-location-option ${executiveLocationType === 'office' ? 'active' : ''}`}
            onClick={() => setExecutiveLocationType('office')}
            disabled={loading || isCapturingLocation}
          >
            Oficina
          </button>
          <button
            type="button"
            className={`time-tracker-location-option ${executiveLocationType === 'campaign' ? 'active' : ''}`}
            onClick={() => setExecutiveLocationType('campaign')}
            disabled={loading || isCapturingLocation}
          >
            Campaña
          </button>
        </div>
        <Typography variant="body2" color="textSecondary">
          {!executiveLocationType
            ? 'Selecciona una opción para habilitar el botón de ingreso.'
            : executiveLocationType === 'office'
            ? `Validaremos que estés dentro de 50 m de ${EXECUTIVE_OFFICES[0].name}.`
            : 'Campaña guarda tu ubicación actual sin validarla contra oficinas.'}
        </Typography>
      </div>
    )
  }

  const renderActionButtons = () => {
    if (!selectedDay || selectedDay !== todayKey) {
      return null
    }

    return (
      <div className="time-tracker-actions">
        {renderExecutiveLocationSelector()}
        {session?.canStart && (
          <Button
            className="time-tracker-pill-button time-tracker-pill-button--primary"
            variant="contained"
            disableElevation
            onClick={() => void handleStartTimer()}
            disabled={loading || isCapturingLocation || (isExecutive && !executiveLocationType)}
          >
            <PlayCircleFilledIcon style={{ marginRight: 8, fontSize: 18 }} />
            {isCapturingLocation ? 'Ubicando...' : 'Ingreso'}
          </Button>
        )}
        {session?.canPauseBreak && (
          <Button
            className="time-tracker-pill-button time-tracker-pill-button--break"
            variant="contained"
            disableElevation
            onClick={() => void pauseTimer('break')}
            disabled={loading}
          >
            <FreeBreakfastIcon style={{ marginRight: 8, fontSize: 18 }} />
            Pausa activa
          </Button>
        )}
        {session?.canPauseLunch && (
          <Button
            className="time-tracker-pill-button time-tracker-pill-button--lunch"
            variant="contained"
            disableElevation
            onClick={() => void pauseTimer('almuerzo')}
            disabled={loading}
          >
            <RestaurantIcon style={{ marginRight: 8, fontSize: 18 }} />
            Pausa almuerzo
          </Button>
        )}
        {session?.canResume && (
          <Button
            className="time-tracker-pill-button time-tracker-pill-button--resume"
            variant="contained"
            disableElevation
            onClick={() => void resumeTimer()}
            disabled={loading}
          >
            <PlayCircleFilledIcon style={{ marginRight: 8, fontSize: 18 }} />
            Reanudar
          </Button>
        )}
        {session?.canStop && (
          <Button
            className="time-tracker-pill-button time-tracker-pill-button--stop"
            variant="contained"
            disableElevation
            onClick={() => setIsStopDialogOpen(true)}
            disabled={loading}
          >
            <StopIcon style={{ marginRight: 8, fontSize: 18 }} />
            Finalizar
          </Button>
        )}
      </div>
    )
  }

  return (
    <div className="time-tracker-page">
      <div className="time-tracker-grid">
        <aside className="time-tracker-sidebar">
          <Typography variant="subtitle2" style={{ color: '#ff6900', marginBottom: 16 }}>
            Semana actual
          </Typography>
          {weekDays.map(day => (
            <button
              key={day.key}
              type="button"
              className={`time-tracker-day-button ${selectedDay === day.key ? 'active' : ''}`}
              onClick={() => setSelectedDay(day.key)}
            >
              <Box display="flex" alignItems="center" justifyContent="space-between" gridGap={8}>
                <Typography variant="subtitle2">{day.label}</Typography>
                {day.isToday ? (
                  <span className={`time-tracker-today-chip ${selectedDay === day.key ? 'active' : ''}`}>Hoy</span>
                ) : null}
              </Box>
              <Typography variant="caption">{day.fullLabel}</Typography>
            </button>
          ))}
        </aside>

        <section>
          <div className="time-tracker-card">
            <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gridGap={16}>
              <div>
                <Typography variant="h5">Registro de horas</Typography>
                <Typography variant="body2" color="textSecondary">
                  Horas restantes de la semana: {formatSeconds(remainingWeekSeconds)}
                </Typography>
              </div>
              {session?.status === 'running' ? (
                <Box bgcolor="#fff2e8" color="#c2410c" padding="8px 14px" borderRadius="999px" fontWeight={600}>
                  {formatSeconds(elapsedToday)}
                </Box>
              ) : null}
            </Box>

            {renderActionButtons()}

            {error ? <div className="time-tracker-alert">{error}</div> : null}

            <div className="time-tracker-summary">
              <div className="time-tracker-summary-card">
                <Typography variant="caption" color="textSecondary">
                  Hora de inicio
                </Typography>
                <Typography variant="h6">{formatTimestamp(selectedEntry?.startedAt || session?.startedAt)}</Typography>
              </div>
              <div className="time-tracker-summary-card">
                <Typography variant="caption" color="textSecondary">
                  Pausas registradas
                </Typography>
                {pauses.length === 0 ? <Typography variant="body2">Sin pausas registradas</Typography> : null}
                {pauses.map(pause => (
                  <div key={pause.pauseId} className="time-tracker-pause-item">
                    <Typography variant="subtitle2">{getPauseLabel(pause.type)}</Typography>
                    <Typography variant="body2">
                      {formatTimestamp(pause.startedAt)} - {formatTimestamp(pause.endedAt)}
                    </Typography>
                  </div>
                ))}
              </div>
              <div className="time-tracker-summary-card">
                <Typography variant="caption" color="textSecondary">
                  Hora de fin
                </Typography>
                <Typography variant="h6">{formatTimestamp(selectedEntry?.endedAt)}</Typography>
              </div>
            </div>
          </div>

          <div className="time-tracker-info" style={{ marginTop: 24 }}>
            <Typography variant="h6">Tabla de horas habilitadas para laborar</Typography>
            <Box marginTop="16px">
              <Typography variant="body2">Lunes a viernes: 7:00 a. m. a 7:00 p. m.</Typography>
              <Typography variant="body2">Sábado: 8:00 a. m. a 12:00 p. m.</Typography>
              <Typography variant="body2">Domingos y festivos: fuera de horario.</Typography>
              <div className="time-tracker-office-note">
                Recuerda que solo puedes registrar tiempo cuando ya te encuentras en la oficina.
              </div>
              {isExecutive && session?.startLocation ? (
                <div className="time-tracker-location-summary">
                  <Typography variant="subtitle2">Ultima ubicacion registrada</Typography>
                  <Typography variant="body2">
                    {session.startLocation.locationType === 'office' ? 'Oficina' : 'Campaña'}{' '}
                    {session.startLocation.address}
                  </Typography>
                  {session.startLocation.nearestOfficeName ? (
                    <Typography variant="body2">
                      Oficina mas cercana: {session.startLocation.nearestOfficeName} (
                      {session.startLocation.distanceToNearestOffice} m)
                    </Typography>
                  ) : null}
                </div>
              ) : null}
            </Box>
          </div>
        </section>
      </div>

      <Dialog
        open={isPermissionDialogOpen}
        onClose={() => !isCapturingLocation && setIsPermissionDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: 24,
            padding: 4
          }
        }}
      >
        <DialogTitle disableTypography>
          <Typography variant="h6" className="time-tracker-stop-dialog-title">
            Habilita tu ubicacion
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className="time-tracker-stop-dialog-copy">{permissionHelpCopy}</div>
          <div className="time-tracker-stop-dialog-highlight">
            Debes permitir acceso a tu ubicacion para registrar horas como ejecutivo.
          </div>
        </DialogContent>
        <DialogActions className="time-tracker-stop-dialog-actions">
          <Button
            onClick={() => setIsPermissionDialogOpen(false)}
            className="time-tracker-stop-dialog-button time-tracker-stop-dialog-button--danger"
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isStopDialogOpen}
        onClose={() => !loading && setIsStopDialogOpen(false)}
        fullWidth
        maxWidth="xs"
        PaperProps={{
          style: {
            borderRadius: 24,
            padding: 4
          }
        }}
      >
        <DialogTitle disableTypography>
          <Typography variant="h6" className="time-tracker-stop-dialog-title">
            Finalizar jornada
          </Typography>
        </DialogTitle>
        <DialogContent>
          <div className="time-tracker-stop-dialog-copy">
            Si finalizas, se dará por terminada tu jornada laboral y no podrás registrar mas horas hoy.
          </div>
          <div className="time-tracker-stop-dialog-highlight">
            Asegurate de haber terminado tus pausas antes de confirmar.
          </div>
        </DialogContent>
        <DialogActions className="time-tracker-stop-dialog-actions">
          <Button
            onClick={() => setIsStopDialogOpen(false)}
            disabled={loading}
            className="time-tracker-stop-dialog-button time-tracker-stop-dialog-button--ghost"
          >
            Cancelar
          </Button>
          <Button
            onClick={() => void handleConfirmStop()}
            disabled={loading}
            className="time-tracker-stop-dialog-button time-tracker-stop-dialog-button--danger"
          >
            Finalizar jornada
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

export default TimeTrackerView
